
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import GenericImageGenerator from '@owc-utils/GenericImage/GenericImageGenerator';
import GenericImageComponent from '@owc-packages/GenericImage/src/GenericImage.vue';
import { SimpleTeaserItemComponent } from '@owc-packages/SimpleTeaser/src/SimpleTeaser';
import { TagType } from './SimpleTeaser.enums';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import Personalisation from '@owc-utils/Mixins/Personalisation';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import { setBrandTheme } from '@owc-utils/Theming/theming';

import { TrackingInstance } from '@owc-packages/Tracking/src/Tracking';
import initializeTracking from '@owc-packages/Tracking/src/TrackingUtils';

let trackingInstance: TrackingInstance | null = null;

export default (ComponentInitializeDispatcher as SimpleTeaserItemComponent).extend({
  name: 'owc-simple-teaser-item',
  components: {
    GenericImageComponent,
    ValidationBoxWrapper,
  },
  mixins: [OwcDataComponent, Personalisation],
  computed: {
    tags: function() {
      return this.personaliseableData?.tags || [];
    },
    hasTags: function() {
      return Boolean(this.tags?.length);
    },
    links: function() {
      return this.personaliseableData?.links;
    },
    image: function() {
      const data = this.personaliseableData;
      if (data?.showMedia && data?.image) {
        return new GenericImageGenerator(
          this.personaliseableData.image,
          ['mq1', 'mq3', 'mq5'],
          '4x3', // this is the ratio that is defined on specs
        );
      }

      return null;
    },
    copy: function() {
      return this.personaliseableData?.copy;
    },
    bullets: function() {
      return this.personaliseableData?.bullets;
    },
    bulletListClasses: function() {
      return [
        'owc-simple-teaser-item__bullet-list',
        'wb-list',
        this.darkMode ? 'wb-list--theme-dark' : '',
        'wb-list--unordered',
      ];
    },
    headline: function() {
      return this.personaliseableData.headline;
    },
    darkMode: function() {
      return Boolean(this.personaliseableData.darkMode);
    },
    variant: function() {
      return this.darkMode ? 'dark-grey' : 'white';
    },
  },
  async mounted() {
    trackingInstance = await initializeTracking();
    this.trackingVisibleItems();

    // To be removed when Workbench releases theming
    // https://pages.git.i.mercedes-benz.com/dh-io-workbench/documentation/v7/foundations/colour-system/
    const componentBrand = (this.$el.getRootNode() as ShadowRoot).host as HTMLDivElement;
    setBrandTheme(componentBrand);
  },
  methods: {
    isCta(ctaType = '') {
      return Boolean(ctaType);
    },
    getCtaLinkClasses: function(ctaLinkType = '') {
      if (!this.isCta(ctaLinkType)) {
        return ['wb-link', this.darkMode ? 'wb-link--theme-dark' : '', 'wb-link--standalone'];
      }

      return [
        'owc-simple-teaser-item-button',
        'wb-button',
        this.darkMode ? 'wb-button--theme-dark' : '',
        `wb-button--${ctaLinkType}`,
        'wb-button--medium',
      ];
    },
    getTagType: function(tagType: number) {
      return TagType[tagType];
    },
    ctaTrackingTrigger(link) {
      const simpleTeaserEl = this.getHost();
      const indexOfItem = [
        ...(simpleTeaserEl?.parentElement?.querySelectorAll(
          'owc-simple-teaser-item:not([class*="aem--is-hidden"])',
        ) ?? []),
      ].indexOf(this.getHost());
      if (indexOfItem >= 0) {
        if (trackingInstance && this.isCta(link.type)) {
          trackingInstance.customTrackingObjectUpdate(
            {
              category: 'link',
              label: `${indexOfItem + 1}.${link.type}.${link.label}`,
              URL: link.url,
            },
            'click',
            this.$el,
          );
        }
      }
    },
    trackingVisibleItems() {
      const observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) {
            this.doubleCtaTrackingTrigger();
            observer.disconnect();
          }
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 0.9,
        },
      );
      observer.observe(this.$refs.sliderItem as HTMLElement);
    },
    doubleCtaTrackingTrigger() {
      const simpleTeaserEl = this.getHost();
      const indexOfItem = [
        ...(simpleTeaserEl?.parentElement?.querySelectorAll(
          'owc-simple-teaser-item:not([class*="aem--is-hidden"])',
        ) ?? []),
      ].indexOf(this.getHost());
      if (indexOfItem >= 0) {
        const ctas = this.links?.filter(link => this.isCta(link.type)) ?? [];
        if (ctas.length > 1) {
          this.handleCustomTracking(this.$el, `card_${indexOfItem + 1}.two_cta.shown`, 'impression', 'click');
        }
      }
    },
  },
});
